import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface TXCupContextType {
  standings: Standing[];
  isLoading: boolean;
  error: string | null;
}

interface Standing {
  name: string;
  totalScore: number | null;
  position: number | null;
}

const TXCupContext = createContext<TXCupContextType>({
  standings: [],
  isLoading: false,
  error: null
});

export function TXCupProvider({ children }: { children: React.ReactNode }) {
  const [standings, setStandings] = useState<Standing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStandings = async () => {
      try {
        if (!supabase) {
          throw new Error('Database connection not available');
        }

        // Get scores from Nov '24 to May '25 for active members only
        const { data: scores, error: scoresError } = await supabase
          .from('historical_scores')
          .select(`
            strokes,
            player:members!inner(
              name,
              current_status
            ),
            tournament:historical_tournaments!inner(
              name,
              date
            )
          `)
          .eq('player.current_status', 'M')
          .gte('tournament.date', '2024-11-01')
          .lte('tournament.date', '2025-10-31')
          .order('date', { foreignTable: 'tournament', ascending: true });

        if (scoresError) throw scoresError;

        // Get all active members
        const { data: members, error: membersError } = await supabase
          .from('members')
          .select('name')
          .eq('current_status', 'M')
          .order('name');

        if (membersError) throw membersError;

        // Group scores by player
        const playerScores: Record<string, number[]> = {};
        scores?.forEach(score => {
          const playerName = score.player.name;
          if (!playerScores[playerName]) {
            playerScores[playerName] = [];
          }
          playerScores[playerName].push(score.strokes);
        });

        // Calculate standings for all active members
        const calculatedStandings = members.map(member => {
          const scores = playerScores[member.name] || [];
          
          // Only calculate total if member has at least 2 scores
          if (scores.length >= 2) {
            const sortedScores = [...scores].sort((a, b) => a - b);
            const lowestTwo = sortedScores.slice(0, 2);
            const total = lowestTwo.reduce((sum, score) => sum + score, 0);
            
            return {
              name: member.name,
              totalScore: total,
              position: null // Will be set after sorting
            };
          }
          
          // Return member with null score if they don't have enough scores
          return {
            name: member.name,
            totalScore: null,
            position: null
          };
        });

        // Sort and assign positions
        const sortedStandings = calculatedStandings
          .sort((a, b) => {
            // Members with scores come first
            if (a.totalScore === null && b.totalScore === null) {
              return a.name.localeCompare(b.name);
            }
            if (a.totalScore === null) return 1;
            if (b.totalScore === null) return -1;
            return a.totalScore - b.totalScore;
          })
          .map((standing, index) => ({
            ...standing,
            position: standing.totalScore !== null ? index + 1 : null
          }));

        setStandings(sortedStandings);
        setError(null);
      } catch (err) {
        console.error('Error loading TX Cup standings:', err);
        setError(err instanceof Error ? err.message : 'Failed to load standings');
      } finally {
        setIsLoading(false);
      }
    };

    loadStandings();
  }, []);

  return (
    <TXCupContext.Provider value={{ standings, isLoading, error }}>
      {children}
    </TXCupContext.Provider>
  );
}

export function useTXCup() {
  const context = useContext(TXCupContext);
  if (!context) {
    throw new Error('useTXCup must be used within a TXCupProvider');
  }
  return context;
}